import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Table, Column } from '../../components/ui/Table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../../services/api';

type OccupancyRow = { id: string; property: string; occupied: number; vacant: number; unitCount: number; occupancyPct: number };

export const Occupancy: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [occupancyData, setOccupancyData] = useState<OccupancyRow[]>([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = (await api.getOccupancyData()) || [];
      const rows: OccupancyRow[] = data.map((p: any, index: number) => {
        const unitCount = (p.occupied || 0) + (p.vacant || 0);
        const occupancyPct = unitCount > 0 ? Math.round(((p.occupied || 0) / unitCount) * 100) : 0;
        return {
          id: p.propertyId ?? p.id ?? p.property ?? `row-${index}`,
          property: p.property,
          occupied: p.occupied || 0,
          vacant: p.vacant || 0,
          unitCount,
          occupancyPct,
        };
      });
      setOccupancyData(rows);
    } catch (err: any) {
      setError(err.message || 'Error al cargar ocupación');
      setOccupancyData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const bestProperty = occupancyData.reduce(
    (best, current) => (current.occupancyPct > best.occupancyPct ? current : best),
    occupancyData[0] || { property: '-', occupancyPct: 0 }
  );
  const worstProperty = occupancyData.reduce(
    (worst, current) => (current.occupancyPct < worst.occupancyPct ? current : worst),
    occupancyData[0] || { property: '-', occupancyPct: 0 }
  );
  const averageOccupancy =
    occupancyData.length > 0
      ? occupancyData.reduce((sum, p) => sum + p.occupancyPct, 0) / occupancyData.length
      : 0;

  const columns: Column<OccupancyRow>[] = [
    { header: 'Propiedad', accessor: 'property', sortable: true },
    { header: 'Unidades Totales', accessor: 'unitCount', sortable: true },
    { header: 'Ocupadas', accessor: 'occupied', sortable: true },
    {
      header: '% Ocupación',
      accessor: (row) => (
        <div className="flex items-center space-x-2">
          <span className="font-medium">{row.occupancyPct}%</span>
          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-accent" style={{ width: `${row.occupancyPct}%` }} />
          </div>
        </div>
      ),
      sortable: true,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <TrendingUp className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Ocupación</h1>
        </div>
        <div className="flex justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <TrendingUp className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Ocupación</h1>
        </div>
        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm font-medium"
        >
          Actualizar
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-sm text-gray-600 mb-1">Mejor Propiedad</div>
          <div className="text-lg font-bold text-primary">{bestProperty.property}</div>
          <div className="text-2xl font-bold text-green-600 mt-2">{bestProperty.occupancyPct}%</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Peor Propiedad</div>
          <div className="text-lg font-bold text-primary">{worstProperty.property}</div>
          <div className="text-2xl font-bold text-red-600 mt-2">{worstProperty.occupancyPct}%</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Promedio General</div>
          <div className="text-2xl font-bold text-primary mt-2">{averageOccupancy.toFixed(1)}%</div>
        </Card>
      </div>

      {occupancyData.length > 0 && (
        <Card title="Ocupación por Propiedad (gráfico)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={occupancyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="property" />
              <YAxis />
              <Tooltip
                formatter={(value: number) => [value, '']}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="occupied" fill="#22c55e" name="Ocupadas" />
              <Bar dataKey="vacant" fill="#e5e7eb" name="Vacantes" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card title="Ocupación por Propiedad">
        {occupancyData.length === 0 ? (
          <p className="text-gray-500 py-4">No hay datos de ocupación.</p>
        ) : (
          <Table data={occupancyData} columns={columns} />
        )}
      </Card>
    </div>
  );
};
